<template>
  <div style="max-width: 960px; margin: 0 auto">
    <div class="vms-guide-hero">
      <h1>How to use Bluezone VMS</h1>
      <p>Click-through tutorial for admins, hosts, and approvers — setup → invite → approve — plus a short script if you want to record a Loom/OBS walkthrough.</p>
      <div class="mt-3">
        <v-btn color="white" class="text-capitalize info--text mr-2" to="/approval-groups">Start setup</v-btn>
        <v-btn outlined color="white" class="text-capitalize" @click="tab = 5">Recording script</v-btn>
      </div>
      <v-card class="mt-4 pa-3" flat style="background: rgba(255,255,255,0.12)">
        <div class="d-flex flex-wrap align-center" style="gap: 8px">
          <v-text-field
            v-model="policyQuestion"
            dense
            hide-details
            outlined
            dark
            placeholder="Ask a quick policy question…"
            class="flex-grow-1"
            style="max-width: 420px"
            @keyup.enter="askPolicy"
          ></v-text-field>
          <v-btn color="white" class="info--text text-capitalize" :loading="policyLoading" @click="askPolicy">
            Ask
          </v-btn>
        </div>
        <p v-if="policyAnswer" class="mb-0 mt-3 white--text" style="opacity: 0.95">{{ policyAnswer }}</p>
      </v-card>
    </div>

    <v-card class="vms-surface" elevation="0">
      <v-card-text>
        <v-tabs v-model="tab" background-color="transparent" color="primary" class="mb-2" show-arrows>
          <v-tab>Tutorial</v-tab>
          <v-tab>Admin setup</v-tab>
          <v-tab>Host invite</v-tab>
          <v-tab>Approver</v-tab>
          <v-tab>Security</v-tab>
          <v-tab>Ops notes</v-tab>
          <v-tab>Recording script</v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">
          <!-- Click-through tutorial -->
          <v-tab-item class="pt-4">
            <p class="mb-4 grey--text">Follow these steps in order. Screenshot boxes are placeholders — replace with real captures when you publish training materials.</p>

            <div v-for="(step, i) in tutorialSteps" :key="step.id" class="vms-tutorial-step mb-6">
              <div class="d-flex align-center mb-2">
                <v-avatar color="info" size="32" class="mr-3 white--text font-weight-bold">{{ i + 1 }}</v-avatar>
                <h3 class="mb-0">{{ step.title }}</h3>
              </div>
              <p class="mb-2">{{ step.body }}</p>
              <ul class="mb-3" v-if="step.bullets">
                <li v-for="(b, bi) in step.bullets" :key="`${step.id}-bullet-${bi}`">{{ b }}</li>
              </ul>
              <div class="vms-shot-placeholder mb-3">
                <v-icon large color="grey lighten-1">mdi-monitor-screenshot</v-icon>
                <div class="mt-2 font-weight-medium">Screenshot placeholder</div>
                <div class="caption grey--text">{{ step.shotHint }}</div>
              </div>
              <v-btn
                v-if="step.route"
                color="info"
                class="text-capitalize"
                :to="step.route"
                small
              >{{ step.cta || 'Open screen' }}</v-btn>
            </div>
          </v-tab-item>

          <v-tab-item class="pt-4">
            <h3 class="mb-2">1. Create approval groups (people)</h3>
            <p>Go to <strong>Approvals → Groups</strong>. For <strong>Head Office</strong>, create groups for: Director – Governor’s Office, Assistant Director – Security, and HQ Security. For <strong>each branch</strong>, create: Branch/Centre Manager, Assistant Administrator, and Branch Security. Assign the right people — do not attach these into workflows.</p>
            <h3 class="mb-2 mt-4">2. Create two workflows (paths)</h3>
            <p>Go to <strong>Approvals → Workflows</strong>. Keep one <strong>Head Office</strong> workflow and one <strong>Branch/Centre</strong> workflow. Steps are <em>roles</em> (not per-branch groups). Adding a new branch only requires new groups for that location.</p>
            <h3 class="mb-2 mt-4">3. Configure notifications</h3>
            <p>Under <strong>Settings → System Configurations</strong>, add SMS keys and Mail SMTP keys (<code>use_custom_mail=YES</code>, then <code>mail_host</code>, <code>mail_username</code>, etc.). Edit copy in <strong>Notification Templates</strong>.</p>
            <h3 class="mb-2 mt-4">4. Assign users</h3>
            <p>Hosts invite visitors. Approvers belong to approval groups. Security officers use the Watchman/Security role (Android check-in stations).</p>
            <v-btn color="primary" class="mt-2" to="/approval-groups">Start with Groups</v-btn>
          </v-tab-item>

          <v-tab-item class="pt-4">
            <h3 class="mb-2">Schedule a visit</h3>
            <ol class="mb-4">
              <li>Open your organization and choose <strong>Schedule Visit</strong>.</li>
              <li>Click <strong>Invite visitor</strong>.</li>
              <li>Enter guest details. Your <strong>location autofills</strong> from your branch (HQ hosts invite to HQ only).</li>
              <li>Optional: click <strong>Improve message</strong> to polish spelling/grammar on the purpose note. For richer AI rewrites, add an <strong>AI / LLM</strong> key under System Configurations.</li>
              <li>Workflow is selected automatically for HQ vs Branch.</li>
              <li>Save — approvers receive a branded email with a dashboard link. The visitor is SMS-invited only after final approval (SMS includes a guest confirm link).</li>
            </ol>
            <p>After approval, use row actions to <strong>Copy invite link</strong> or <strong>Print badge</strong>. Guests open <code>/guest/{token}</code> to confirm details and show a QR at reception.</p>
            <p class="mt-2">Admins can switch Organization/Branch to any location in their memberships.</p>
          </v-tab-item>

          <v-tab-item class="pt-4">
            <h3 class="mb-2">Approve or reject</h3>
            <ol>
              <li>Open the email notification or go to Today’s Visits.</li>
              <li>Open the visit. Approve / Reject appear only if you are in the current step’s group.</li>
              <li>On final approval, Host and Security are emailed and the visitor receives the invite SMS (with guest confirm link).</li>
              <li>On rejection, the Host is emailed with the reason.</li>
            </ol>
          </v-tab-item>

          <v-tab-item class="pt-4">
            <h3 class="mb-2">Check-in / check-out</h3>
            <p>Use the Android Security app on the sign-in workstations. Expected visitors appear after approval. Capture a portrait photo on check-in when required. Multi-gate facilities follow security levels (SL1 then SL2+).</p>
            <p class="mt-2">On successful check-in, the <strong>host is notified</strong> (email + SMS templates: <code>visit.checked_in_host</code>) in addition to any existing CheckIn SMS.</p>
            <p class="mt-2">Hardware tips: 23" monitor station + webcam for portraits; tablet + pen for signatures; use <strong>Print badge</strong> on Schedule Visit (or <code>GET api/vms/visits/{id}/badge</code>) for sticker printers.</p>
          </v-tab-item>

          <v-tab-item class="pt-4">
            <h3 class="mb-2">Mail &amp; SMS credentials</h3>
            <ul>
              <li><strong>SMS (type 1)</strong>: Africa’s Talking or custom gateway keys.</li>
              <li><strong>Mail SMTP (type 5)</strong>: <code>use_custom_mail</code> = <code>YES</code>, then host / port / username / password / encryption / from fields.</li>
              <li><strong>AI / LLM (type 6)</strong>: OpenAI-compatible. Key = API base URL, Value = secret, Identifier = model. Optional <code>LLM_*</code> / <code>OPENAI_*</code> entries in <code>.env</code> fill any empty field. Invite assist falls back to local polish without a key.</li>
            </ul>
            <h3 class="mb-2 mt-4">Template placeholders</h3>
            <p>
              <code v-pre>{{guest_name}}</code>,
              <code v-pre>{{host_name}}</code>,
              <code v-pre>{{facility_name}}</code>,
              <code v-pre>{{organization_name}}</code>,
              <code v-pre>{{event_date}}</code>,
              <code v-pre>{{event_time}}</code>,
              <code v-pre>{{invite_url}}</code>,
              <code v-pre>{{checked_in_at}}</code>,
              <code v-pre>{{approver_name}}</code>,
              <code v-pre>{{security_name}}</code>,
              <code v-pre>{{id_number}}</code>,
              <code v-pre>{{reason}}</code>
            </p>
            <h3 class="mb-2 mt-4">Guest link &amp; risk</h3>
            <p>Set <code>FRONTEND_URL</code> on the API so SMS invite links point at your web app. Visit risk flags (blacklist, after-hours, missing ID) are available via <code>GET api/vms/visits/{id}/risk</code>.</p>
          </v-tab-item>

          <!-- Recording script -->
          <v-tab-item class="pt-4">
            <h3 class="mb-2">3–5 minute Loom / OBS script</h3>
            <p class="mb-4 grey--text">Speak the lines below while clicking the actions. Target length: about 4 minutes.</p>

            <v-alert dense text type="info" class="mb-4" border="left">
              Prep: log in as admin, have one branch ready, Mail/SMS configured if you want to show notifications.
            </v-alert>

            <div v-for="(beat, i) in recordingScript" :key="`recording-beat-${beat.title || i}`" class="mb-5">
              <div class="vms-page-kicker mb-1">{{ beat.time }}</div>
              <h4 class="mb-1">{{ beat.title }}</h4>
              <p class="mb-1"><strong>Click:</strong> {{ beat.click }}</p>
              <p class="mb-0"><strong>Say:</strong> “{{ beat.say }}”</p>
            </div>

            <v-divider class="my-4"></v-divider>

            <h4 class="mb-2">When you export the video</h4>
            <ul class="mb-2">
              <li><strong>Resolution:</strong> 1080p (1920 × 1080).</li>
              <li><strong>Cursor:</strong> turn cursor highlight on in Loom or OBS so clicks are easy to follow.</li>
              <li><strong>Framing:</strong> crop to the browser window only — hide the desktop and other apps.</li>
              <li><strong>File title:</strong> use <em>Bluezone VMS — Setup, invite, approve</em> so people can find it later.</li>
            </ul>
            <p class="caption grey--text mb-0">
              Aim for about 4 minutes. Pause briefly between sections so viewers can keep up.
            </p>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import VmsService from '../../services/vms.service';

export default {
  name: "PlatformGuide",
  data: () => ({
    tab: 0,
    policyQuestion: '',
    policyAnswer: '',
    policyLoading: false,
    tutorialSteps: [
      {
        id: "groups",
        title: "Create approval groups",
        body: "Groups assign people to process roles. HQ has Governor’s Office, AD Security, and HQ Security. Each branch has Manager, Assistant Admin, and Branch Security.",
        bullets: [
          "Approvals → Groups → New",
          "Pick Branch (or HQ) and the process role",
          "Add approvers → Submit",
        ],
        shotHint: "Capture: Approval Groups list with New dialog open",
        route: "/approval-groups",
        cta: "Open Groups",
      },
      {
        id: "workflows",
        title: "Build HQ + Branch workflows",
        body: "Two workflows only. Steps are roles; the system resolves the correct people for the visit’s branch.",
        bullets: [
          "Approvals → Workflows",
          "One Head Office path and one Branch/Centre path",
          "Roles come prefilled — no need to attach every branch group",
        ],
        shotHint: "Capture: Workflow form with role steps and HQ/Branch switch",
        route: "/workflows",
        cta: "Open Workflows",
      },
      {
        id: "invite",
        title: "Invite a guest",
        body: "Hosts (and admins) invite from Schedule Visit. Location and workflow autofill.",
        bullets: [
          "Organizations → open a branch → Schedule Visit",
          "Invite visitor → fill guest details → Save",
          "Approvers are emailed; guest SMS waits for final approval",
        ],
        shotHint: "Capture: Invite visitor dialog with autofilled location/workflow",
        route: "/organisations",
        cta: "Open Organizations",
      },
      {
        id: "approve",
        title: "Approve the visit",
        body: "Approvers act from Today’s Visits or the email link.",
        bullets: [
          "Filter “Needs my approval” if helpful",
          "Open visit → Approve or Reject",
          "Final approval notifies Host, Security, and SMS to the guest",
        ],
        shotHint: "Capture: Visit preview with Approve / Reject buttons",
        route: "/organisations",
        cta: "Go to Organizations",
      },
    ],
    recordingScript: [
      {
        time: "0:00 – 0:20",
        title: "Open & framing",
        click: "Land on Dashboard, then open the side menu briefly.",
        say: "This is Bluezone Visitor Management. In under five minutes I’ll show setup, inviting a guest, and approval.",
      },
      {
        time: "0:20 – 1:10",
        title: "Approval groups",
        click: "Approvals → Groups → New. Pick HQ or a branch, choose a process role, add approvers, save.",
        say: "First, approval groups. These assign people to roles like Branch Manager or HQ Security. Create the three HQ roles, then the three roles for each branch.",
      },
      {
        time: "1:10 – 2:00",
        title: "Workflows",
        click: "Approvals → Workflows. Show the HQ workflow and Branch workflow with role steps — not a long list of every branch group.",
        say: "Next, workflows. You only need two: Head Office and Branch. Steps are roles. When a visit is booked at Kisumu, the system uses Kisumu’s Manager, Admin, and Security groups automatically.",
      },
      {
        time: "2:00 – 3:10",
        title: "Invite visitor",
        click: "Organizations → open a branch → Schedule Visit → Invite visitor. Fill name and phone, show autofill, Save.",
        say: "Now invite a guest. Location and workflow fill in automatically. Save — approvers get an email. The visitor only gets an SMS after final approval.",
      },
      {
        time: "3:10 – 4:20",
        title: "Approve",
        click: "Open Today’s Visits, filter Needs my approval if useful, Preview, Approve.",
        say: "Approvers open Today’s Visits, preview the request, and approve. After the last step, Host and Security are emailed and the guest receives their invite SMS.",
      },
      {
        time: "4:20 – 4:45",
        title: "Close",
        click: "Optional: open Help → Tutorial tab.",
        say: "That’s setup, invite, and approve. Full click-through steps are under Help whenever you need them.",
      },
    ],
  }),
  methods: {
    async askPolicy() {
      const question = (this.policyQuestion || '').trim();
      if (!question) {
        this.policyAnswer = 'Type a question about inviting, approving, or setup.';
        return;
      }
      this.policyLoading = true;
      try {
        const response = await VmsService.policyAnswer({ question });
        const data = response?.data?.data ?? response?.data ?? {};
        this.policyAnswer = data.answer || 'No answer returned.';
      } catch (e) {
        this.policyAnswer = e.response?.data?.message || 'Could not answer right now. Browse the tabs below.';
      } finally {
        this.policyLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.vms-tutorial-step h3 {
  font-size: 1.1rem;
  font-weight: 700;
}
.vms-shot-placeholder {
  border: 2px dashed #d0d5dd;
  border-radius: 10px;
  background: #f8fafc;
  padding: 28px 16px;
  text-align: center;
  color: #6b7280;
}
</style>
